///
/// EarthBrowser
///
/// Created by Matt Giger on 9/18/2026
/// Copyright (c) 2026 Geodata Labs
///
/// RTK Query endpoints for user dataset cloud storage
/// (/api/user/datasets). Uploads go directly from the browser to
/// Backblaze B2: upload-init reserves quota and returns a temporary
/// upload URL, the file goes up via XHR (for progress events), and
/// upload-complete verifies and activates the record.
///

import type {
	DatasetUploadCompleteRequest,
	DatasetUploadCompleteResponse,
	DatasetUploadInitRequest,
	DatasetUploadInitResponse,
	ListUserDatasetsResponse,
	UserDatasetBounds,
	UserDatasetFormat,
	UserDatasetRecord,
} from "@shared-types"
import { authenticatedApi } from "./API/authenticatedApi"

export interface UploadDatasetArgs {
	file: File
	name: string
	format: UserDatasetFormat
	featureCount?: number
	/** Ground overlay placement (required for "image") */
	bounds?: UserDatasetBounds
	onProgress?: (fraction: number) => void
}

const cContentTypes: Record<UserDatasetFormat, string> = {
	geojson: "application/geo+json",
	csv: "text/csv",
	image: "image/png",
}

async function sha1Hex(file: File): Promise<string> {
	const buffer = await file.arrayBuffer()
	const digest = await crypto.subtle.digest("SHA-1", buffer)
	return Array.from(new Uint8Array(digest))
		.map(byte => byte.toString(16).padStart(2, "0"))
		.join("")
}

/** Direct browser → B2 upload (Native API) with progress callbacks */
function uploadToB2(
	init: DatasetUploadInitResponse,
	file: File,
	contentType: string,
	sha1: string,
	onProgress?: (fraction: number) => void
): Promise<void> {
	return new Promise<void>((resolve, reject) => {
		const xhr = new XMLHttpRequest()

		xhr.upload.addEventListener("progress", event => {
			if (event.lengthComputable && onProgress) {
				onProgress(event.loaded / event.total)
			}
		})
		xhr.addEventListener("load", () => {
			if (xhr.status >= 200 && xhr.status < 300) {
				resolve()
			} else {
				reject(new Error(`Upload to storage failed (${xhr.status})`))
			}
		})
		xhr.addEventListener("error", () => reject(new Error("Network error during upload")))
		xhr.addEventListener("abort", () => reject(new Error("Upload cancelled")))

		xhr.open("POST", init.upload_url)
		xhr.setRequestHeader("Authorization", init.authorization_token)
		xhr.setRequestHeader("X-Bz-File-Name", encodeURIComponent(init.b2_key).replace(/%2F/g, "/"))
		xhr.setRequestHeader("Content-Type", contentType)
		xhr.setRequestHeader("X-Bz-Content-Sha1", sha1)
		xhr.send(file)
	})
}

export const userDatasetsApi = authenticatedApi.injectEndpoints({
	endpoints: (builder) => ({
		listUserDatasets: builder.query<ListUserDatasetsResponse, void>({
			query: () => ({ url: "/api/user/datasets" }),
			providesTags: ["UserDatasets"],
		}),

		deleteUserDataset: builder.mutation<{ success: boolean }, string>({
			query: (id) => ({
				url: `/api/user/datasets/${id}`,
				method: "DELETE",
			}),
			invalidatesTags: ["UserDatasets"],
		}),

		uploadUserDataset: builder.mutation<UserDatasetRecord, UploadDatasetArgs>({
			queryFn: async ({ file, name, format, featureCount, bounds, onProgress }, _api, _extra, baseQuery) => {
				// 1. Reserve quota, get the direct upload URL
				const initBody: DatasetUploadInitRequest = {
					name,
					format,
					filename: file.name,
					size_bytes: file.size,
					feature_count: featureCount,
					bounds,
				}
				const initResult = await baseQuery({
					url: "/api/user/datasets/upload-init",
					method: "POST",
					body: initBody,
				})
				if (initResult.error) {
					return { error: initResult.error }
				}
				const init = initResult.data as DatasetUploadInitResponse

				// 2. Upload directly to B2 (SHA1 required by the Native API)
				try {
					const sha1 = await sha1Hex(file)
					await uploadToB2(init, file, file.type || cContentTypes[format], sha1, onProgress)
				} catch (error) {
					return {
						error: {
							status: "CUSTOM_ERROR",
							error: error instanceof Error ? error.message : "Upload failed",
						},
					}
				}

				// 3. Verify + activate
				const completeBody: DatasetUploadCompleteRequest = { dataset_id: init.dataset_id }
				const completeResult = await baseQuery({
					url: "/api/user/datasets/upload-complete",
					method: "POST",
					body: completeBody,
				})
				if (completeResult.error) {
					return { error: completeResult.error }
				}
				return { data: (completeResult.data as DatasetUploadCompleteResponse).dataset }
			},
			invalidatesTags: ["UserDatasets"],
		}),
	}),
})

export const {
	useListUserDatasetsQuery,
	useDeleteUserDatasetMutation,
	useUploadUserDatasetMutation,
} = userDatasetsApi
